import { useState } from 'react';
import { useCalibrationState } from '../../context/CalibrationPageContext';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import { saveCalibrations } from '../../services/api';


function PreviewSensors() {
  const {
    sensors,
    setSensors,
    removeSensor,
    showPreview
  } = useCalibrationState();

  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text }

  const handleUpload = async () => {
    // Flatten the per-type queues into the flat list the endpoint expects.
    const payload = Object.entries(sensors).flatMap(([sensorType, list]) =>
      list.map((s) => ({
        sensorType,
        serialNumber: s.SerialNumber,
        calibrationFactor: s.CalibrationFactor,
        unit: s.CalibrationFactorUnit,
        testDate: s.TestDate,
      }))
    );
    if (payload.length === 0) return;

    setSaving(true);
    setFeedback(null);
    try {
      const res = await saveCalibrations(payload);
      setSensors({ Loadcells: [], PressureGauges: [], LVDTSensors: [], StrainGauges: [] });
      setFeedback({ type: 'success', text: `Saved ${res.saved} sensor${res.saved === 1 ? '' : 's'}.` });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      // direction='columnn'
      // display='flex'
      sx={{
        p: 1,
        width: { xs: '100%', md: '70%' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >

      <Box
        sx={{
          // height: { xl: `calc(100% - 64px)`, lg: `calc(100% - 57px)`},
          height: { xs: 'auto', md: 'calc(100% - 36px)' },
          minHeight: { xs: 120, md: 0 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          pt: 1,
          // mt: showPreview ? 0 : 3,
        }}
      >
        <Grid
          container
          alignItems='center'
          display={showPreview ? 'flex' : 'none'}
          sx={{
            borderRadius: 1,
            backgroundColor: 'secondary.main',
            color: 'white',
            pr: 1,
            mb: 0.5
          }}
        >
          <Grid item sm={4} justifyItems='center'>
            <Typography variant='body1' sx={{ fontSize: { md: 14, sm: 10 } }}>
              Serial Number
            </Typography>
          </Grid>

          <Grid item sm={3} justifyItems='center'>
            <Typography variant='body1' sx={{ fontSize: { md: 14, sm: 10 } }}>
              Cal Factor
            </Typography>
          </Grid>

          <Grid item sm={4} justifyItems='center'>
            <Typography variant='body1' sx={{ fontSize: { md: 14, sm: 10 } }}>
              Test Date
            </Typography>
          </Grid>
        </Grid>


        <Box sx={{ height: { xs: 'auto', md: 'calc(100% - 28px)' }, maxHeight: { xs: 320, md: 'none' }, overflow: 'auto', scrollbarWidth: 'thin' }}>
          {Object.keys(sensors).map((type) => (
            sensors[type].length > 0 && (
              <Stack
                direction='column'
                spacing={1}
                justifyContent='center'
                sx={{ mb: { xl: 2, lg: 1 } }}
              >
                <Stack
                  direction='row'
                  display='flex'
                  alignItems='center'
                  sx={{ mb: 0.5 }}
                >
                  <Divider
                    orientation='horizontal'
                    sx={{
                      width: { md: 56, sm: 32 },
                      mr: 1,
                      borderBottom: 2
                    }}
                  />

                  <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 12 } }}>
                    {type}
                  </Typography>

                  <Divider
                    orientation='horizontal'
                    sx={{
                      flexGrow: 1,
                      ml: 1,
                      borderBottom: 2,
                    }}
                  />
                </Stack>

                {sensors[type].map((sensor, idx) => (
                  <Grid
                    container
                    alignItems='center'
                    sx={{
                      border: '1px solid',
                      borderRadius: { md: 1, sm: 0.75 },
                      borderColor: 'divider'
                    }}
                  >
                    <Grid item sm={4} justifyItems='center'>
                      <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 10 } }}>
                        {sensor['SerialNumber']}
                      </Typography>
                    </Grid>

                    <Grid item sm={3} justifyItems='center'>
                      <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 10 } }}>
                        {sensor['CalibrationFactor']}
                        {sensor['CalibrationFactorUnit'] && (
                          <Typography
                            component='span'
                            sx={{ fontSize: { xl: 13, md: 11, sm: 9 }, color: 'text.secondary', ml: 0.5 }}
                          >
                            {sensor['CalibrationFactorUnit']}
                          </Typography>
                        )}
                      </Typography>
                    </Grid>

                    <Grid item sm={4} justifyItems='center'>
                      <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 10 } }}>
                        {sensor['TestDate']}
                      </Typography>
                    </Grid>

                    <Grid item sm={1} display='flex' justifyItems='center' alignItems='center'>
                      <IconButton
                        size='small'
                        onClick={() => removeSensor(type, idx)}
                        sx={{
                          p: 0.25,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: { xl: 24, md: 21, sm: 12 } }}/>
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            )
          ))}
        </Box>
      </Box>

      <Box
        display='flex'
        alignItems='center'
        justifyContent='right'
        sx={{
          height: '28px',
          mt: 1,
          gap: 1,
        }}
      >
        {feedback && (
          <Typography
            variant='caption'
            sx={{ color: feedback.type === 'success' ? 'success.main' : 'error.main' }}
          >
            {feedback.text}
          </Typography>
        )}
        <Button
          size='small'
          variant='contained'
          disabled={!showPreview || saving}
          onClick={handleUpload}
          sx={{
            height: '28px',
            fontSize: '12px',
            py: 0.5,
            px: 1
          }}
        >
          {saving ? <CircularProgress size={14} color='inherit' /> : 'Upload Sensors'}
        </Button>
      </Box>
    </Box>
  );
};

export default PreviewSensors;